import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock, Route as RouteIcon } from "lucide-react";
import { useLang, waLink } from "@/i18n";
import { TOURS } from "@/data";
import { INCLUSIONS, TOUR_DETAILS } from "@/tourDetails";
import { Seo } from "@/components/Seo";
import { Kicker, Reveal } from "@/components/Reveal";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import RoyalAssistant from "@/components/RoyalAssistant";

const SITE = "https://www.exploreindians.com";

const TourDetail = () => {
  const { id } = useParams();
  const { lang, t } = useLang();
  const tour = TOURS.find((x) => x.id === id);
  const details = tour ? TOUR_DETAILS[tour.id] : null;

  const jsonLd = useMemo(() => {
    if (!tour || !details) return null;
    return {
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      name: tour.title.en,
      description: details.overview.en,
      url: `${SITE}/tours/${tour.id}`,
      image: tour.image.startsWith("/") ? `${SITE}${tour.image}` : tour.image,
      touristType: "Luxury private travellers",
      itinerary: {
        "@type": "ItemList",
        itemListElement: details.days.map((d, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `Day ${i + 1} — ${d.en}`,
        })),
      },
      provider: {
        "@type": "TravelAgency",
        name: "Explore India",
        url: SITE,
        telephone: "+919252211011",
      },
    };
  }, [tour, details]);

  if (!tour || !details) {
    return (
      <main className="bg-obsidian min-h-screen text-slate-100">
        <Header />
        <div className="pt-40 pb-32 text-center px-4" data-testid="tour-not-found">
          <h1 className="font-display text-4xl">Tour not found</h1>
          <a href="/#tours" className="mt-6 inline-block text-gold-light underline">{t.td.back}</a>
        </div>
        <Footer />
      </main>
    );
  }

  const title = `${tour.title.en} — ${tour.duration.en} | Explore India`;
  const canonical = `${SITE}/tours/${tour.id}`;

  return (
    <main className="bg-obsidian min-h-screen text-slate-100" data-testid={`tour-detail-${tour.id}`}>
      <Seo
        title={title}
        description={details.overview.en}
        canonical={canonical}
        image={tour.image.startsWith("/") ? `${SITE}${tour.image}` : tour.image}
        jsonLd={jsonLd}
      />
      <Header />

      <section className="relative h-[58vh] min-h-[420px] overflow-hidden">
        <img src={tour.image} alt={tour.title[lang]} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/45 to-[#0B0C10]/30" />
        <div className="absolute bottom-0 inset-x-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-12">
            <Reveal y={20}>
              <Link to="/" data-testid="tour-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-mono text-gold/90 hover:text-gold-light">
                <ArrowLeft size={13} /> {t.td.back}
              </Link>
              <span className="mt-4 inline-block rounded-full bg-[#0B0C10]/75 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-gold ring-1 ring-gold/40">
                {tour.tag[lang]}
              </span>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-50 leading-tight max-w-3xl">
                {tour.title[lang]}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-300 font-mono">
                <span className="inline-flex items-center gap-2"><Clock size={15} className="text-gold" />{tour.duration[lang]}</span>
                <span className="inline-flex items-center gap-2"><RouteIcon size={15} className="text-gold" />{tour.route[lang]}</span>
                <span className="inline-flex items-center gap-2 font-display text-lg text-gold-light" data-testid="tour-detail-price">
                  {tour.price ? tour.price[lang] : t.tours.priceOnRequest}
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-3 gap-14">
          <div className="lg:col-span-2">
            <Reveal>
              <Kicker>{t.td.overview}</Kicker>
              <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed" data-testid="tour-overview">
                {details.overview[lang]}
              </p>
            </Reveal>

            <Reveal delay={0.1} className="mt-14">
              <Kicker>{t.td.itinerary}</Kicker>
              <ol className="mt-8 space-y-0" data-testid="tour-itinerary">
                {details.days.map((d, i) => (
                  <li key={i} className="relative pl-14 pb-8 border-l border-gold/20 last:pb-0 ml-3">
                    <span className="absolute -left-3 top-0 flex h-6 w-6 items-center justify-center rounded-full bg-gold/15 ring-1 ring-gold/50 font-mono text-[11px] text-gold">
                      {i + 1}
                    </span>
                    <p className="text-xs uppercase tracking-[0.25em] font-mono text-gold/80">{t.td.day} {i + 1}</p>
                    <p className="mt-1.5 text-sm sm:text-base text-slate-300 leading-relaxed">{d[lang]}</p>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <aside className="lg:sticky lg:top-28 glass-card rounded-2xl border border-gold/15 p-7 sm:p-9 h-fit">
              <Kicker>{t.td.highlights}</Kicker>
              <ul className="mt-5 space-y-3">
                {tour.highlights[lang].map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                    {h}
                  </li>
                ))}
              </ul>

              <div className="my-7 border-t border-gold/10" />

              <Kicker>{t.td.inclusions}</Kicker>
              <ul className="mt-5 space-y-3">
                {INCLUSIONS[lang].map((inc) => (
                  <li key={inc} className="flex items-start gap-3 text-sm text-slate-400">
                    <Check size={14} className="mt-0.5 shrink-0 text-gold" />
                    {inc}
                  </li>
                ))}
              </ul>

              <div className="mt-8 space-y-3">
                <a
                  href="/#enquiry"
                  data-testid="tour-detail-enquire-btn"
                  className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-amber px-6 py-3.5 text-sm font-bold text-obsidian hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-shadow"
                >
                  {t.td.enquire} <ArrowRight size={15} />
                </a>
                <a
                  href={waLink(2, `Namaste Explore India! I would like a quote for the "${tour.title.en}" tour (${tour.duration.en}, ${tour.route.en}).`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tour-detail-whatsapp-btn"
                  className="flex items-center justify-center gap-2 rounded-full border border-gold/40 px-6 py-3.5 text-sm font-semibold text-gold-light hover:bg-gold/10 transition-colors"
                >
                  {t.tours.whatsapp}
                </a>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
      <RoyalAssistant />
    </main>
  );
};

export default TourDetail;
